import React, { useState, useEffect } from "react";
import NewTransactionManager from "../DataArchive/TransactionManager/NewTransactionManager";
import { useDispatch, useSelector } from "react-redux";
import { fetchJobDocuments } from "src/redux/reducers/JobDocument";
import { useLocation, useHistory, Link } from "react-router-dom";
import { fetchTotals } from "src/redux/reducers/Reconciler";
import { Tab, Tabs, Badge, Button, Form, Modal, Row, Col } from "react-bootstrap";
import { FraudData } from "../FraudViewer";
import { AnalyzeTabGated, CategorizeTabGated } from "../Analyze";
import { useQueryStateSync } from "../../../custom-hooks/useQueryStateSync";
import { fetchMfs } from "src/redux/reducers/TransactionManager";
import { ReduxState } from "src/redux";
import { useRecentDocument } from "src/hooks/useRecentNavigation";
import { moveDocuments, downloadDocument } from "src/docuclipper/api";
import { saveAs } from "file-saver";
import { createAlert } from "src/redux/reducers/Alerts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { JobDocument } from "src/docuclipper/DocuclipperTypes";

export const BankDocumentsListWithMove: React.FC<{
  customerId: string;
  projectId: string;
  /** When provided, used for refetch after move and for empty-state label (e.g. "invoiceMode", "formMode", "generic", "template", "brokerageMode"). */
  fieldName?: "bankMode" | "invoiceMode" | "formMode" | "generic" | "template" | "brokerageMode";
}> = ({ customerId, projectId, fieldName = "bankMode" }) => {
  const dispatch = useDispatch();
  const { data: jobDocs, loading } = useSelector(
    (state: ReduxState) => state.JobDocument
  );
  const { tagLevels } = useSelector((state: ReduxState) => state.TagLevel);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState("");
  const [targetProjectId, setTargetProjectId] = useState("");
  const [moving, setMoving] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; doc: JobDocument } | null>(null);

  // Ensure we fetch documents for this doc type when the list is shown
  useEffect(() => {
    if (customerId && projectId) {
      dispatch(fetchJobDocuments({ fieldName }));
    }
  }, [customerId, projectId, fieldName, dispatch]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [contextMenu]);

  const projectOptions: Array<{ folderId: string; projectId: string; folderName: string; projectName: string }> = [];
  if (tagLevels?.root) {
    Object.entries(tagLevels.root).forEach(([fId, folderData]: [string, any]) => {
      Object.entries(folderData).forEach(([pId, projectData]: [string, any]) => {
        if (pId !== "metadata" && projectData?.metadata?.tagId) {
          if (fId !== customerId || pId !== projectId) {
            projectOptions.push({ folderId: fId, projectId: pId, folderName: fId, projectName: pId });
          }
        }
      });
    });
  }

  const sourceTagId = tagLevels?.root?.[customerId]?.[projectId]?.metadata?.tagId;
  const targetTagId =
    targetFolderId && targetProjectId
      ? tagLevels?.root?.[targetFolderId]?.[targetProjectId]?.metadata?.tagId
      : null;

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    if (!jobDocs?.length) return;
    if (selectedIds.size === jobDocs.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(jobDocs.map((d) => d.documentId)));
  };

  const handleMove = async () => {
    if (!sourceTagId || !targetTagId || selectedIds.size === 0) return;
    setMoving(true);
    try {
      const result = await moveDocuments(
        targetTagId,
        sourceTagId,
        Array.from(selectedIds).map(Number)
      );
      setShowMoveModal(false);
      setSelectedIds(new Set());
      setTargetFolderId("");
      setTargetProjectId("");
      dispatch(fetchJobDocuments({ fieldName }));
      dispatch(createAlert({ type: "success", message: `Moved ${result.movedCount ?? selectedIds.size} document(s)`, timeout: 5000 }));
    } catch (e) {
      dispatch(createAlert({ type: "error", message: e instanceof Error ? e.message : "Failed to move documents. Please try again.", timeout: 5000 }));
    } finally {
      setMoving(false);
    }
  };

  const openMoveForDoc = (doc: JobDocument) => {
    setSelectedIds(new Set([doc.documentId]));
    setContextMenu(null);
    setShowMoveModal(true);
  };

  if (loading) return <p className="text-muted">Loading documents…</p>;
  const docs = jobDocs ?? [];

  return (
    <div className="mt-3">
      {selectedIds.size > 0 && (
        <div className="d-flex align-items-center justify-content-between py-2 px-3 mb-2 rounded bg-light border">
          <span className="fw-medium">
            {selectedIds.size} selected
          </span>
          <div className="d-flex gap-2">
            <Button variant="outline-dark" size="sm" onClick={selectAll}>
              {selectedIds.size === docs.length ? "Deselect all" : "Select all"}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowMoveModal(true)}
            >
              <FontAwesomeIcon icon="exchange-alt" className="mx-1" />
              Move to project
            </Button>
          </div>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted small">
          {docs.length} document(s)
        </span>
        {docs.length > 0 && (
          <Button variant="outline-dark" size="sm" className="text-dark" onClick={selectAll}>
            {selectedIds.size === docs.length ? "Deselect all" : "Select all"}
          </Button>
        )}
      </div>
      <div className="list-group">
        {docs.length === 0 ? (
          <div className="list-group-item text-muted d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "0.5rem" }}>
            <span>
              {fieldName === "bankMode"
                ? "No bank or credit card documents yet. Upload a PDF to get started."
                : fieldName === "brokerageMode"
                ? "No brokerage statements yet. Upload a PDF to get started."
                : "No documents yet. Upload a PDF to get started."}
            </span>
            {fieldName === "brokerageMode" && (
              <Button
                variant="primary"
                size="sm"
                as={Link as any}
                to={`/extract/bankMode?l1=${encodeURIComponent(customerId)}&l2=${encodeURIComponent(projectId)}&subType=brokerage`}
              >
                <FontAwesomeIcon icon="upload" className="mr-1" />
                Upload Brokerage Statement
              </Button>
            )}
          </div>
        ) : (
          docs.map((doc: JobDocument) => (
            <div
              key={doc.documentId}
              className="list-group-item list-group-item-action d-flex align-items-center"
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, doc });
              }}
            >
              <Form.Check
                type="checkbox"
                checked={selectedIds.has(doc.documentId)}
                onChange={(ev) => {
                  ev.stopPropagation();
                  toggleSelect(doc.documentId);
                }}
                onClick={(ev) => ev.stopPropagation()}
                className="mx-3"
              />
              <span className="flex-grow-1 text-truncate" title={doc.originalname}>
                {doc.originalname}
              </span>
              <span className="text-muted small mx-2">{doc.numPages} page(s)</span>
              <Button
                variant="outline-dark"
                size="sm"
                onClick={(ev) => {
                  ev.stopPropagation();
                  downloadDocument(Number(doc.documentId))
                    .then((blob) => {
                      const name = doc.originalname || `document-${doc.documentId}`;
                      saveAs(blob, name);
                      dispatch(createAlert({ type: "success", message: "Document downloaded.", timeout: 3000 }));
                    })
                    .catch((err) => {
                      dispatch(createAlert({ type: "error", message: err?.message || "Error downloading document. Please try again.", timeout: 5000 }));
                    });
                }}
              >
                <FontAwesomeIcon icon="download" className="mx-1" />
                Download
              </Button>
            </div>
          ))
        )}
      </div>

      {contextMenu && (
        <div
          className="border rounded shadow-sm bg-white py-1 position-fixed"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1060,
            minWidth: 180,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="dropdown-item py-2 text-start border-0 bg-transparent w-100"
            onClick={() => {
              const doc = contextMenu.doc;
              setContextMenu(null);
              downloadDocument(Number(doc.documentId))
                .then((blob) => {
                  const name = doc.originalname || `document-${doc.documentId}`;
                  saveAs(blob, name);
                  dispatch(createAlert({ type: "success", message: "Document downloaded.", timeout: 3000 }));
                })
                .catch((err) => {
                  dispatch(createAlert({ type: "error", message: err?.message || "Error downloading document. Please try again.", timeout: 5000 }));
                });
            }}
          >
            <FontAwesomeIcon icon="download" className="mx-2" />
            Download
          </button>
          <button
            type="button"
            className="dropdown-item py-2 text-start border-0 bg-transparent w-100"
            onClick={() => openMoveForDoc(contextMenu.doc)}
          >
            <FontAwesomeIcon icon="exchange-alt" className="mx-2" />
            Move to project
          </button>
        </div>
      )}

      <Modal show={showMoveModal} onHide={() => !moving && setShowMoveModal(false)}>
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder=""
        >
          <Modal.Title>Move documents to project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Target folder</Form.Label>
            <Form.Control
              as="select"
              value={targetFolderId}
              onChange={(e) => {
                setTargetFolderId(e.target.value);
                setTargetProjectId("");
              }}
            >
              <option value="">Select folder…</option>
              {Array.from(new Set(projectOptions.map((p) => p.folderId))).map((fid) => (
                <option key={fid} value={fid}>
                  {fid}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>Target project</Form.Label>
            <Form.Control
              as="select"
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              disabled={!targetFolderId}
            >
              <option value="">Select project…</option>
              {projectOptions
                .filter((p) => p.folderId === targetFolderId)
                .map((p) => (
                  <option key={`${p.folderId}::${p.projectId}`} value={p.projectId}>
                    {p.projectName}
                  </option>
                ))}
            </Form.Control>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMoveModal(false)} disabled={moving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleMove}
            disabled={!targetTagId || moving}
          >
            {moving ? "Moving…" : "Move"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export const Transactions = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const { queryParams, querySetters } = useQueryStateSync({
    mode: "reconcile",
    analyzeMode: "categorize",
  });

  const mode = queryParams.mode;
  const [activeTab, setActiveTab] = useState(() => {
    switch (mode) {
      case "categorize":
        return "2";
      case "analyze":
        return "3";
      case "fraud":
        return "4";
      case "documents":
        return "5";
      default:
        return "1";
    }
  });

  const { selectedL1, selectedL2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);

  // Track this page view as a recent document
  useRecentDocument(
    selectedL1 && selectedL2
      ? {
          docType: "bank-statements",
          label: `Bank Transactions - ${selectedL2}`,
          icon: "university",
          l1: selectedL1,
          l2: selectedL2,
        }
      : null
  );

  // React.useEffect(() => {
  //   dispatch(clearJobData());
  // }, []);

  React.useEffect(() => {
    dispatch(
      fetchJobDocuments({
        fieldName: "bankMode",
      })
    );
    dispatch(fetchMfs());
    dispatch(fetchTotals());
  }, [job?.id, selectedL1, selectedL2]);

  useEffect(() => {
    const tab = mode === "documents" ? "5" : mode === "categorize" ? "2" : mode === "analyze" ? "3" : mode === "fraud" ? "4" : "1";
    setActiveTab(tab);
  }, [mode]);

  return (
    <div className="p-4">
      <style>{`
        #transaction-tabs .nav-link {
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--bs-body-color);
          font-weight: 500;
        }
        #transaction-tabs .nav-link.active {
          background-color: transparent !important;
          color: var(--bs-primary) !important;
          border-bottom-color: var(--bs-primary);
        }
        #transaction-tabs .nav-link:hover:not(.active) {
          background-color: rgba(0,0,0,0.06);
        }
      `}</style>
      {/* <div style={{ float: "right", marginBottom: "8px" }}>
        <Button
          variant="link"
          className="mr-2 d-inline-flex flex-column align-items-center"
          onClick={() => {
            history.push(`${location.pathname}${location.search}&mode=analyze`);
          }}
        >
          <span>Advanced Analysis</span>
          <small className="text-muted">
            Flow of funds & cashflow analysis
          </small>
        </Button>
        <Button
          variant="link"
          className="d-inline-flex flex-column align-items-center"
          onClick={() => {
            history.push(`${location.pathname}${location.search}&mode=fraud`);
          }}
        >
          <span>Fraud Detection</span>
          <small className="text-muted">Identify suspicious patterns</small>
        </Button>
      </div> */}

      <Tabs
        activeKey={activeTab}
        id="transaction-tabs"
        onSelect={(tab) => {
          setActiveTab(tab || "1");
          if (tab === "2") {
            querySetters.set_mode("categorize");
            querySetters.set_analyzeMode("categorize");
          } else if (tab === "1") {
            querySetters.set_mode("reconcile");
            querySetters.set_analyzeMode("reconcile");
          } else if (tab === "3") {
            querySetters.set_mode("analyze");
            querySetters.set_analyzeMode("flow");
          } else if (tab === "4") {
            querySetters.set_mode("fraud");
            querySetters.set_analyzeMode("fraud");
          } else if (tab === "5") {
            querySetters.set_mode("documents");
          }
        }}
      >
        <Tab eventKey="1" title="Transactions">
          {activeTab === "1" && (
            <NewTransactionManager hideJobButtons={false} />
          )}
        </Tab>
        <Tab
          eventKey="2"
          title={
            <>
              <span>Categorize</span>
            </>
          }
        >
          {activeTab === "2" && <CategorizeTabGated />}
        </Tab>
        <Tab
          eventKey="3"
          title={
            <div className="d-flex align-items-center">
              <span>Advanced Analysis</span>
              <Badge
                variant="secondary"
                className="ml-2"
                style={{ fontSize: "0.7em" }}
              >
                Business
              </Badge>
            </div>
          }
        >
          {activeTab === "3" && <AnalyzeTabGated />}
        </Tab>
        <Tab
          eventKey="4"
          title={
            <div className="d-flex align-items-center">
              <span>Fraud Detection</span>
              <Badge
                variant="secondary"
                className="ml-2"
                style={{ fontSize: "0.7em" }}
              >
                Business
              </Badge>
            </div>
          }
        >
          {activeTab === "4" && <FraudData />}
        </Tab>
        <Tab eventKey="5" title="Documents">
          {activeTab === "5" && selectedL1 && selectedL2 && (
            <BankDocumentsListWithMove customerId={selectedL1} projectId={selectedL2} />
          )}
        </Tab>
      </Tabs>
    </div>
  );
};
